import { useEffect, useState } from "react";

/**
 * Custom hook to detect touch devices on the client side
 * Uses multiple detection methods for better reliability
 */
export const useClientTouchDetection = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const detectTouchDevice = () => {
      // Method 1: CSS media query check (most reliable)
      const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
      const hasFinePointer = window.matchMedia("(pointer: fine)").matches;

      // If we have a clear pointer indication, use that
      if (hasCoarsePointer) {
        setIsTouchDevice(true);
        return;
      }

      if (hasFinePointer && !hasCoarsePointer) {
        // Fine pointer and not coarse = likely desktop
        setIsTouchDevice(false);
        return;
      }

      // Method 2: Check hover capability (touch devices typically can't hover)
      const canHover = window.matchMedia("(hover: hover)").matches;
      const cannotHover = window.matchMedia("(hover: none)").matches;

      if (cannotHover || (!canHover && hasCoarsePointer)) {
        setIsTouchDevice(true);
        return;
      }

      // Method 3: Fallback - User agent patterns for tablets that might not be detected by CSS
      const userAgent = navigator.userAgent.toLowerCase();
      const tabletPatterns = [
        /tablet/i,
        /ipad/i,
        /android(?!.*mobile)/i, // Android without "mobile" often indicates tablet
        /kindle/i,
        /silk/i,
        /playbook/i,
        /bb10/i,
      ];
      const isTabletByUA = tabletPatterns.some((pattern) =>
        pattern.test(userAgent)
      );

      if (isTabletByUA) {
        setIsTouchDevice(true);
        return;
      }

      // Method 4: Last resort - Touch events support, but be conservative
      // Only use this if we can't determine from CSS queries and no fine pointer
      const hasTouchSupport =
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-expect-error - for older browsers
        navigator.msMaxTouchPoints > 0;

      // Only consider touch support if we don't have fine pointer capabilities
      if (hasTouchSupport && !hasFinePointer) {
        setIsTouchDevice(true);
        return;
      }

      // Default to false for desktop-like devices
      setIsTouchDevice(false);
    };

    // Initial detection
    detectTouchDevice();

    // Listen for changes in pointer and hover media queries
    const pointerQuery = window.matchMedia("(pointer: coarse)");
    const hoverQuery = window.matchMedia("(hover: hover)");
    const handleChange = () => detectTouchDevice();

    // Modern browsers
    if (pointerQuery.addEventListener) {
      pointerQuery.addEventListener("change", handleChange);
      hoverQuery.addEventListener("change", handleChange);
      return () => {
        pointerQuery.removeEventListener("change", handleChange);
        hoverQuery.removeEventListener("change", handleChange);
      };
    }
    // Legacy browsers
    else if (pointerQuery.addListener) {
      pointerQuery.addListener(handleChange);
      hoverQuery.addListener(handleChange);
      return () => {
        pointerQuery.removeListener(handleChange);
        hoverQuery.removeListener(handleChange);
      };
    }
  }, []);

  return isTouchDevice;
};
